/**
 * Supabase Realtime cross-user sync (Layer 2)
 *
 * Subscribes to postgres_changes on key tables and invalidates the
 * corresponding TanStack Query caches. Uses a 300ms debounce per table
 * to batch rapid-fire events (e.g. bulk inserts).
 */

import type { QueryClient } from '@tanstack/react-query'
import type { SupabaseClient } from '@supabase/supabase-js'

/**
 * Maps each Realtime-watched table to the query key prefixes that
 * should be invalidated when a row in that table changes.
 */
const TABLE_INVALIDATION_MAP: Record<string, readonly (readonly string[])[]> = {
  allocations: [
    ['allocations'],
    ['map-allocations'],
    ['aircraft-registry'],
  ],
  transient_stays: [
    ['transient-stays'],
    ['map-allocations'],
    ['allocations'],
    ['client-registry'],
  ],
  aircraft_schedules: [
    ['aircraft-schedules'],
    ['flight-line-schedules'],
    ['map-allocations'],
    ['aircraft-flights'],
    ['location-schedules'],
  ],
  allocation_events: [
    ['aircraft-movement-log'],
    ['map-allocations'],
    ['aircraft-schedules'],
    ['flight-line-schedules'],
    ['aircraft-flights'],
  ],
} as const

const DEBOUNCE_MS = 300

interface RealtimeSyncOptions {
  supabase: SupabaseClient
  queryClient: QueryClient
  schema?: string
}

/**
 * Subscribe to Supabase Realtime postgres_changes for the configured tables.
 * Returns an unsubscribe function.
 */
export function createRealtimeSync({
  supabase,
  queryClient,
  schema = 'public',
}: RealtimeSyncOptions): () => void {
  const debounceTimers = new Map<string, ReturnType<typeof setTimeout>>()

  const channel = supabase.channel('db-sync')

  for (const table of Object.keys(TABLE_INVALIDATION_MAP)) {
    channel.on(
      'postgres_changes' as any,
      { event: '*', schema, table },
      () => {
        // Debounce per table to batch rapid-fire events
        const existing = debounceTimers.get(table)
        if (existing) clearTimeout(existing)

        debounceTimers.set(
          table,
          setTimeout(() => {
            debounceTimers.delete(table)
            const keys = TABLE_INVALIDATION_MAP[table]
            for (const key of keys) {
              queryClient.invalidateQueries({ queryKey: [...key] })
            }
          }, DEBOUNCE_MS),
        )
      },
    )
  }

  channel.subscribe((status) => {
    if (status === 'SUBSCRIBED') {
      console.log('[Realtime] db-sync channel subscribed')
    } else if (status === 'CHANNEL_ERROR') {
      console.warn('[Realtime] db-sync channel error')
    } else if (status === 'TIMED_OUT') {
      console.warn('[Realtime] db-sync channel timed out')
    }
  })

  return () => {
    // Clear pending debounce timers
    for (const timer of debounceTimers.values()) clearTimeout(timer)
    debounceTimers.clear()
    supabase.removeChannel(channel)
  }
}
